import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
} from 'lucide-react';
import { usePhotoCarouselApi } from '@/hooks/usePhotoCarousel';
import type { SlimRelease } from '@/lib/api';
import {
  fetchMusicCatalog,
  fetchPhoto,
  fetchPhotos,
  fetchReleaseById,
  flattenMusicCatalog,
  getCachedMusicReleases,
  getCachedPhotos,
  preloadMediaItems,
  preloadMusicCatalog,
  preloadPhotoCover,
  preloadReleaseArtwork,
} from '@/lib/api';
import { paths } from '@/lib/routes';
import {
  releaseArtworkImageUrl,
  resetPageMeta,
  setPageMeta,
} from '@/lib/pageMeta';
import { absoluteUrl } from '@/lib/share';
import { navigateViewTransition, runViewTransition } from '@/lib/modeNav';
import { linkifySocialHtml } from '@/lib/linkifySocial';
import {
  formatPhotoPostDate,
  parsePhotoCaption,
  photoDisplayTitle,
  stripLeadingPhotoTitle,
} from '@/lib/photoCaption';
import type { PhotoPost, Release, Track } from '@/types';
import { TrackList } from '@/components/list/TrackList';
import {
  hasReleaseInfo,
  ReleaseInfoButton,
} from '@/components/list/ReleaseInfoPopover';
import {
  ReleasePlayButton,
  ReleaseShuffleButton,
} from '@/components/list/ReleasePlayButton';
import { MusicVideoShelf } from '@/components/list/MusicVideoShelf';
import { ReleaseMetaDetails } from '@/components/list/ReleaseMetaDetails';
import {
  PhotoLocationLink,
  PhotoMetaDetails,
} from '@/components/list/PhotoMetaDetails';
import {
  ListSkeleton,
  PhotoDetailSkeleton,
  ReleaseDetailSkeleton,
} from '@/components/ui/skeleton';
import { ScrollingTitle } from '@/components/ui/ScrollingTitle';
import { Button } from '@/components/ui/button';
import { CatalogFormatLabel } from '@/components/list/CatalogFormatLabel';
import { cn } from '@/lib/utils';
import {
  estimateCatalogSkeletonRows,
  useCatalogRowFit,
} from '@/hooks/useCatalogRowFit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePlayer } from '@/hooks/usePlayer';

/** Match catalog query against title + plain description (HTML stripped). */
export function photoMatchesQuery(
  post: Pick<PhotoPost, 'title' | 'description'>,
  q: string,
): boolean {
  if (!q) return true;
  const displayTitle = photoDisplayTitle(post);
  const desc = String(post.description || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ');
  return `${displayTitle} ${post.title} ${desc}`.toLowerCase().includes(q);
}

type MusicProps = {
  onSelectRelease: (release: Release | null) => void;
  selectedId: string | null;
  onVideo: (url: string, track: Track) => void;
  activeVideoUrl?: string | null;
};

export function MusicList({
  onSelectRelease,
  selectedId,
  onVideo,
  activeVideoUrl,
}: MusicProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [items, setItems] = useState<SlimRelease[]>(
    () => getCachedMusicReleases() ?? [],
  );
  const [loading, setLoading] = useState(
    () => getCachedMusicReleases() == null,
  );
  const [detail, setDetail] = useState<Release | null>(null);
  const [filter, setFilter] = useState('All');
  const [filterGen, setFilterGen] = useState(0);
  const { setReleasePlayback, toggleTrack } = usePlayer();
  const scrollRef = useRef<HTMLDivElement>(null);
  const workspaceFitRef = useRef<HTMLElement | null>(null);
  const hydratedReleaseId = useRef<string | null>(null);
  /** Catalog list scroll — restored after detail closes (list unmounts while open). */
  const listScrollTop = useRef(0);
  /** Consume `?track=` deep-link once per release open. */
  const deepLinkTrackConsumed = useRef<string | null>(null);
  const [tracksReady, setTracksReady] = useState(false);

  const onFilterChange = useCallback((value: string) => {
    setFilter(value);
    setFilterGen((g) => g + 1);
  }, []);

  useEffect(() => {
    if (!filterGen) return;
    listScrollTop.current = 0;
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [filterGen]);

  const shellFromSlim = useCallback((item: SlimRelease): Release => {
    return {
      ...item,
      tracks: [],
      description: '',
      credits: undefined,
      // Keep Listen platforms from the slim catalog — don’t wait on full hydrate.
      streamLinks: item.streamLinks,
    };
  }, []);

  /** Prefer hydrated detail; fall back to catalog slim so push VT can paint immediately. */
  const activeDetail = useMemo(() => {
    if (!selectedId) return null;
    if (detail?.id === selectedId) return detail;
    const slim = items.find((i) => i.id === selectedId);
    return slim ? shellFromSlim(slim) : null;
  }, [selectedId, detail, items, shellFromSlim]);

  const focusOpen = Boolean(activeDetail);

  const openRelease = useCallback(
    (item: SlimRelease) => {
      listScrollTop.current = scrollRef.current?.scrollTop ?? listScrollTop.current;
      void (async () => {
        await preloadReleaseArtwork(item);
        const shell = shellFromSlim(item);
        runViewTransition(() => {
          setTracksReady(false);
          hydratedReleaseId.current = null;
          setDetail(shell);
          onSelectRelease(shell);
          navigate(paths.musicRelease(item.id));
        }, 'forward');
      })();
    },
    [navigate, onSelectRelease, shellFromSlim],
  );

  // Sync shell/full to App before paint so the media stage has artwork in the VT frame.
  useLayoutEffect(() => {
    if (!selectedId || !activeDetail || activeDetail.id !== selectedId) return;
    onSelectRelease(activeDetail);
  }, [selectedId, activeDetail, onSelectRelease]);

  // Keep OG/Twitter image on the release artwork for in-app + share scrapers.
  useEffect(() => {
    if (!selectedId || !activeDetail || activeDetail.id !== selectedId) {
      resetPageMeta();
      return;
    }
    const artist = activeDetail.artist?.trim() || 'Zachary Gray';
    setPageMeta({
      title: activeDetail.name,
      description: [artist, activeDetail.type].filter(Boolean).join(' · '),
      image: releaseArtworkImageUrl(activeDetail.id, activeDetail.artworkUrl),
      url: absoluteUrl(paths.musicRelease(activeDetail.id)),
    });
    return () => {
      resetPageMeta();
    };
  }, [selectedId, activeDetail]);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedMusicReleases();
    if (cached) {
      setItems(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchMusicCatalog()
      .then((data) => {
        if (cancelled) return;
        setItems(flattenMusicCatalog(data));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Paint a catalog shell as soon as the slim row is available (direct load).
  useEffect(() => {
    if (!selectedId) return;
    const slim = items.find((i) => i.id === selectedId);
    if (!slim) return;
    setDetail((prev) => {
      if (prev?.id === selectedId && (prev.tracks?.length ?? 0) > 0) return prev;
      return shellFromSlim(slim);
    });
  }, [selectedId, items, shellFromSlim]);

  useEffect(() => {
    if (!selectedId) {
      hydratedReleaseId.current = null;
      setTracksReady(false);
      setDetail(null);
      return;
    }

    if (hydratedReleaseId.current === selectedId) return;

    let cancelled = false;
    setTracksReady(false);
    (async () => {
      try {
        // Prefer a type hint so we hit one endpoint. Direct loads often race
        // ahead of React `items` — use cache / in-flight catalog first.
        let typeHint =
          items.find((i) => i.id === selectedId)?.type ||
          getCachedMusicReleases()?.find((i) => i.id === selectedId)?.type;
        if (!typeHint) {
          try {
            const catalog = await preloadMusicCatalog();
            if (cancelled) return;
            typeHint = flattenMusicCatalog(catalog).find(
              (i) => i.id === selectedId,
            )?.type;
          } catch {
            /* probe without hint */
          }
        }
        if (cancelled) return;
        const full = await fetchReleaseById(selectedId, typeHint);
        if (cancelled) return;
        hydratedReleaseId.current = selectedId;
        setDetail(full);
        onSelectRelease(full);
        setTracksReady(true);
        setReleasePlayback({
          tracks: full.tracks || [],
          releaseId: full.id,
          albumName: full.name,
          artist: full.artist,
          type: full.type,
          artwork: full.artworkUrl,
          credits: full.credits,
          streamLinks: full.streamLinks,
        });
      } catch (e) {
        console.error(e);
        if (cancelled) return;
        hydratedReleaseId.current = null;
        setTracksReady(false);
        navigateViewTransition(navigate, paths.music(), 'back', {
          replace: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, navigate, setReleasePlayback]);

  // Share deep-link: `/music/:id?track=N` → play that track once.
  useEffect(() => {
    if (!selectedId || !detail || detail.id !== selectedId) return;
    const trackParam = new URLSearchParams(location.search).get('track');
    if (!trackParam) return;
    const trackPos = Number(trackParam);
    const deepKey = `${selectedId}:${trackParam}`;
    if (
      !Number.isFinite(trackPos) ||
      trackPos <= 0 ||
      deepLinkTrackConsumed.current === deepKey
    ) {
      return;
    }
    deepLinkTrackConsumed.current = deepKey;
    toggleTrack(trackPos, { adoptPending: true });
    navigate(paths.musicRelease(selectedId), { replace: true });
  }, [selectedId, detail, location.search, toggleTrack, navigate]);

  const closeDetail = useCallback(() => {
    void (async () => {
      // Deep-link back: catalog must be ready so the VT snapshots the list, not a skeleton.
      if (getCachedMusicReleases() == null && items.length === 0) {
        try {
          const data = await fetchMusicCatalog();
          setItems(flattenMusicCatalog(data));
          setLoading(false);
        } catch {
          /* still attempt back */
        }
      }
      runViewTransition(() => {
        navigate(paths.music());
        onSelectRelease(null);
        setDetail(null);
        hydratedReleaseId.current = null;
      }, 'back');
    })();
  }, [navigate, onSelectRelease, items.length]);

  const types = useMemo(
    () => ['All', ...Array.from(new Set(items.map((i) => i.type)))],
    [items],
  );

  const visible = useMemo(() => {
    return items.filter((r) => filter === 'All' || r.type === filter);
  }, [items, filter]);

  // Fit during loading too so skeleton rows match final catalog height.
  useCatalogRowFit(scrollRef, Math.max(visible.length, 1), !focusOpen);

  // Restore list scroll after detail unmounts (after row-fit layout).
  useLayoutEffect(() => {
    if (focusOpen || loading || selectedId) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = listScrollTop.current;
  }, [focusOpen, loading, selectedId, visible.length]);

  // Mobile release only: pack track rows to the workspace band (header→footer).
  // Desktop keeps natural row height — no stretch to the dock.
  useLayoutEffect(() => {
    workspaceFitRef.current = document.querySelector<HTMLElement>('.workspace');
  }, [focusOpen, isMobile]);

  const releaseTrackCount = activeDetail?.tracks?.length ?? 0;
  useCatalogRowFit(
    workspaceFitRef,
    Math.max(releaseTrackCount, tracksReady ? 0 : 8),
    Boolean(isMobile && focusOpen && activeDetail),
    {
      rowSelector: '.track-row, .skeleton-track-row',
      cssVar: '--track-row-height',
      minRow: 44,
      // No y-snap on release detail — proximity snap yanked the row up on play.
      snap: false,
    },
  );

  // Direct `/music/:id` — detail chrome skeleton, not the catalog list.
  // Detail must win over `loading` so deep-links never flash list skeleton.
  if (selectedId && !activeDetail) {
    return <ReleaseDetailSkeleton />;
  }

  if (focusOpen && activeDetail) {
    const year = new Date(activeDetail.releaseDate).getFullYear();
    const tracks = activeDetail.tracks || [];
    const totalSeconds = tracks.reduce((sum, t) => sum + (t.duration || 0), 0);
    const songLabel = tracks.length === 1 ? 'Song' : 'Songs';
    const releaseDateLabel = (() => {
      const d = new Date(activeDetail.releaseDate);
      if (!Number.isFinite(d.getTime())) return null;
      return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    })();
    const durationLabel = (() => {
      if (totalSeconds <= 0) return null;
      const minutes = Math.max(1, Math.round(totalSeconds / 60));
      return `${minutes} ${minutes === 1 ? 'Minute' : 'Minutes'}`;
    })();
    const phonogramYear = Number.isFinite(year) ? year : null;
    const labelText = activeDetail.label?.trim() || null;
    const aboutHtml = activeDetail.description?.trim()
      ? linkifySocialHtml(activeDetail.description.trim())
      : '';

    return (
      <div className="detail-view">
        <div className="detail-scroll">
          <div className="detail-align">
            <div className="detail-heading">
              <div className="detail-heading-lead">
                <ScrollingTitle
                  as="h1"
                  text={activeDetail.name}
                  className="detail-title"
                />
                <div className="detail-title-actions detail-title-actions--desktop">
                  <button
                    type="button"
                    className="detail-title-back"
                    aria-label="Back to all music"
                    onClick={closeDetail}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <ReleasePlayButton
                    tracks={tracks}
                    className="detail-title-play"
                  />
                  {hasReleaseInfo(
                    activeDetail.credits,
                    activeDetail.streamLinks,
                    isMobile,
                  ) ? (
                    <ReleaseInfoButton
                      credits={activeDetail.credits}
                      streamLinks={activeDetail.streamLinks}
                      className="detail-title-info"
                      side="bottom"
                      align="end"
                    />
                  ) : null}
                </div>
              </div>
              <p className="detail-artist">
                {activeDetail.artist?.trim() || 'Zachary Gray'}
              </p>
              <div
                className="detail-heading-transport"
                aria-label="Release controls"
              >
                <button
                  type="button"
                  className="detail-title-back"
                  aria-label="Back to all music"
                  onClick={closeDetail}
                >
                  <ChevronLeft size={18} />
                </button>
                <ReleasePlayButton
                  tracks={tracks}
                  showLabel
                  className="detail-title-play detail-title-play--labeled"
                />
                <ReleaseShuffleButton tracks={tracks} />
                {hasReleaseInfo(
                  activeDetail.credits,
                  activeDetail.streamLinks,
                  isMobile,
                ) ? (
                  <ReleaseInfoButton
                    credits={activeDetail.credits}
                    streamLinks={activeDetail.streamLinks}
                    className="detail-title-info"
                    side="bottom"
                    align="end"
                  />
                ) : (
                  <span
                    className="detail-title-transport-slot"
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
            <TrackList
              tracks={tracks}
              albumArtist={activeDetail.artist}
              isCompilation={/compilation/i.test(activeDetail.type || '')}
              loading={!tracksReady}
              afterTracks={
                <>
                  <MusicVideoShelf
                    tracks={tracks}
                    artwork={activeDetail.artworkUrl}
                    artist={activeDetail.artist}
                    activeVideoUrl={activeVideoUrl}
                    onVideo={onVideo}
                  />
                  <div className="release-apple-meta" aria-label="Release details">
                    {releaseDateLabel ? <p>{releaseDateLabel}</p> : null}
                    {tracksReady && tracks.length > 0 ? (
                      <p>
                        {tracks.length} {songLabel}
                        {durationLabel ? `, ${durationLabel}` : ''}
                      </p>
                    ) : null}
                    {tracksReady && (phonogramYear != null || labelText) ? (
                      <p
                        className="release-apple-circle"
                        aria-label={
                          phonogramYear != null
                            ? `℗ ${phonogramYear}${labelText ? ` ${labelText}` : ''}`
                            : labelText || undefined
                        }
                      >
                        {phonogramYear != null ? (
                          <>
                            <span className="phonogram" aria-hidden="true">
                              <span>P</span>
                            </span>
                            <span>
                              {phonogramYear}
                              {labelText ? ` ${labelText}` : ''}
                            </span>
                          </>
                        ) : (
                          labelText
                        )}
                      </p>
                    ) : null}
                  </div>
                  {aboutHtml ? (
                    <div
                      className="release-about"
                      dangerouslySetInnerHTML={{ __html: aboutHtml }}
                    />
                  ) : null}
                  <ReleaseMetaDetails
                    release={activeDetail}
                    className="release-meta-inline"
                  />
                </>
              }
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <div className="catalog-toolbar">
          <div className="section-label">
            Discography
            <span>--</span>
          </div>
          <div className="catalog-filter is-skeleton" aria-hidden>
            <span className="skeleton skeleton-filter" />
          </div>
        </div>
        <div className="catalog-column-labels">
          <span>Year</span>
          <span>Release</span>
          <span>Format</span>
          <span />
        </div>
        <div
          className="catalog-scroll"
          ref={scrollRef}
          aria-busy="true"
          aria-label="Loading music"
        >
          <ListSkeleton rows={estimateCatalogSkeletonRows()} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="catalog-toolbar">
        <div className="section-label">
          Discography
          <span>{items.length.toString().padStart(2, '0')}</span>
        </div>
        <div className="catalog-filter">
          <select
            aria-label="Filter catalog"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
          >
            {types.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDown
            className="select-chevron"
            size={14}
            aria-hidden
            strokeWidth={2}
          />
        </div>
      </div>
      <div className="catalog-column-labels">
        <span>Year</span>
        <span>Release</span>
        <span>Format</span>
        <span />
      </div>
      <div
        className="catalog-scroll"
        ref={scrollRef}
        onScroll={(e) => {
          listScrollTop.current = e.currentTarget.scrollTop;
        }}
      >
        {visible.map((item, i) => {
          const year = new Date(item.releaseDate).getFullYear();
          return (
            <Link
              key={`${filterGen}-${item.id}`}
              to={paths.musicRelease(item.id)}
              className={`catalog-row${filterGen > 0 ? ' catalog-row-filter-in' : ''}`}
              style={{ '--row-i': i } as CSSProperties}
              onClick={(e) => {
                e.preventDefault();
                openRelease(item);
              }}
            >
              <span className="row-year">{year}</span>
              <span className="row-title">{item.name}</span>
              <CatalogFormatLabel label={item.type} />
              <ArrowUpRight size={16} />
            </Link>
          );
        })}
        {!visible.length ? (
          <p
            key={`empty-${filterGen}`}
            className={`empty-state${filterGen > 0 ? ' catalog-row-filter-in' : ''}`}
          >
            No results.
          </p>
        ) : null}
      </div>
    </>
  );
}

export function PhotoList({
  onSelectPhoto,
  selectedId,
}: {
  onSelectPhoto: (post: PhotoPost | null) => void;
  selectedId: string | null;
}) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [items, setItems] = useState<PhotoPost[]>(() => getCachedPhotos() ?? []);
  const [detail, setDetail] = useState<PhotoPost | null>(null);
  const [loading, setLoading] = useState(() => getCachedPhotos() == null);
  const [filter, setFilter] = useState('All');
  const [filterGen, setFilterGen] = useState(0);
  const hydratedPhotoId = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Catalog list scroll — restored after detail closes. */
  const listScrollTop = useRef(0);

  const onFilterChange = useCallback((value: string) => {
    setFilter(value);
    setFilterGen((g) => g + 1);
  }, []);

  useEffect(() => {
    if (!filterGen) return;
    listScrollTop.current = 0;
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [filterGen]);

  const activeDetail = useMemo(() => {
    if (!selectedId) return null;
    if (detail?.id === selectedId) return detail;
    return items.find((i) => i.id === selectedId) ?? null;
  }, [selectedId, detail, items]);

  const focusOpen = Boolean(activeDetail);

  const openPhoto = useCallback(
    (item: PhotoPost) => {
      listScrollTop.current = scrollRef.current?.scrollTop ?? listScrollTop.current;
      void (async () => {
        await preloadPhotoCover(item);
        runViewTransition(() => {
          setDetail(item);
          onSelectPhoto(item);
          navigate(paths.photoPost(item.id));
        }, 'forward');
      })();
    },
    [navigate, onSelectPhoto],
  );

  useLayoutEffect(() => {
    if (!selectedId || !activeDetail || activeDetail.id !== selectedId) return;
    onSelectPhoto(activeDetail);
  }, [selectedId, activeDetail, onSelectPhoto]);

  useEffect(() => {
    let cancelled = false;
    const cached = getCachedPhotos();
    if (cached) {
      setItems(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPhotos()
      .then((list) => {
        if (cancelled) return;
        setItems(list);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Catalog shell for direct `/photo/:id` while the full post hydrates.
  useEffect(() => {
    if (!selectedId) return;
    const slim = items.find((i) => i.id === selectedId);
    if (!slim) return;
    setDetail((prev) => {
      if (prev?.id === selectedId && (prev.media?.length ?? 0) > 0) return prev;
      return slim;
    });
  }, [selectedId, items]);

  useEffect(() => {
    if (!selectedId) {
      hydratedPhotoId.current = null;
      setDetail(null);
      return;
    }

    if (hydratedPhotoId.current === selectedId) return;

    let cancelled = false;
    (async () => {
      try {
        const full = await fetchPhoto(selectedId);
        if (cancelled) return;
        hydratedPhotoId.current = selectedId;
        setDetail(full);
        onSelectPhoto(full);
        preloadMediaItems(full.media);
      } catch (e) {
        console.error(e);
        if (cancelled) return;
        hydratedPhotoId.current = null;
        setDetail(null);
        navigateViewTransition(navigate, paths.photo(), 'back', {
          replace: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, navigate]);

  const closeDetail = useCallback(() => {
    void (async () => {
      if (getCachedPhotos() == null && items.length === 0) {
        try {
          const list = await fetchPhotos();
          setItems(list);
          setLoading(false);
        } catch {
          /* still attempt back */
        }
      }
      runViewTransition(() => {
        navigate(paths.photo());
        onSelectPhoto(null);
        setDetail(null);
        hydratedPhotoId.current = null;
      }, 'back');
    })();
  }, [navigate, onSelectPhoto, items.length]);

  const types = useMemo(
    () => ['All', ...Array.from(new Set(items.map((i) => i.format).filter(Boolean)))],
    [items],
  );

  const visible = useMemo(() => {
    return items.filter((p) => filter === 'All' || p.format === filter);
  }, [items, filter]);

  useCatalogRowFit(scrollRef, Math.max(visible.length, 1), !focusOpen);

  useLayoutEffect(() => {
    if (focusOpen || loading || selectedId) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = listScrollTop.current;
  }, [focusOpen, loading, selectedId, visible.length]);

  const photoCarousel = usePhotoCarouselApi();

  // Direct `/photo/:id` — detail must win over `loading`.
  if (selectedId && !activeDetail) {
    return <PhotoDetailSkeleton />;
  }

  if (focusOpen && activeDetail) {
    const { albumTitle, body: captionBody } = parsePhotoCaption(
      activeDetail.description,
    );
    const displayTitle = albumTitle || activeDetail.title;
    const postDateLabel = formatPhotoPostDate(activeDetail.date);
    // Title is shown in the heading — never repeat it (or the blank under it) in the body.
    const desc = stripLeadingPhotoTitle(captionBody, displayTitle).trim();
    const slideCount = photoCarousel?.count ?? 0;
    const slideIndex = photoCarousel?.index ?? 0;
    const canSlide = Boolean(photoCarousel?.canSlide);
    const iconBtn = (extra?: string) =>
      cn('detail-title-action', extra);
    const photoPlaying = Boolean(photoCarousel?.playing);

    return (
      <div className="detail-view photo-detail-view">
        <div className="detail-scroll">
          <div className="detail-align">
            <div className="detail-heading">
              <div className="detail-heading-lead">
                <ScrollingTitle
                  as="h1"
                  text={displayTitle}
                  className="detail-title"
                />
                <div className="detail-title-actions detail-title-actions--desktop">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={iconBtn('detail-title-back')}
                    aria-label="Back to all photos"
                    onClick={closeDetail}
                  >
                    <ChevronLeft size={18} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={iconBtn('detail-title-play')}
                    aria-label={
                      photoPlaying ? 'Pause slideshow' : 'Play slideshow'
                    }
                    disabled={slideCount <= 1}
                    onClick={() => photoCarousel?.togglePlay()}
                  >
                    {photoPlaying ? (
                      <Pause size={16} fill="currentColor" />
                    ) : (
                      <Play size={16} fill="currentColor" />
                    )}
                  </Button>
                  {slideCount > 1 ? (
                    <div
                      className="detail-title-stepper"
                      role="group"
                      aria-label="Photo navigation"
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={iconBtn('detail-title-prev')}
                        aria-label="Previous image"
                        disabled={!canSlide}
                        onClick={() => photoCarousel?.prev()}
                      >
                        <ChevronLeft size={18} />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={iconBtn('detail-title-next')}
                        aria-label="Next image"
                        disabled={!canSlide}
                        onClick={() => photoCarousel?.next()}
                      >
                        <ChevronRight size={18} />
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
              {postDateLabel ? (
                <p className="detail-artist">{postDateLabel}</p>
              ) : null}
              <div
                className="detail-heading-transport"
                aria-label="Photo controls"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={iconBtn('detail-title-back')}
                  aria-label="Back to all photos"
                  onClick={closeDetail}
                >
                  <ChevronLeft size={18} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    iconBtn('detail-title-play detail-title-play--labeled'),
                  )}
                  aria-label={
                    photoPlaying ? 'Pause slideshow' : 'Play slideshow'
                  }
                  disabled={slideCount <= 1}
                  onClick={() => photoCarousel?.togglePlay()}
                >
                  {photoPlaying ? (
                    <Pause size={16} fill="currentColor" />
                  ) : (
                    <Play size={16} fill="currentColor" />
                  )}
                  <span>{photoPlaying ? 'Pause' : 'Play'}</span>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={iconBtn('detail-title-prev')}
                  aria-label="Previous image"
                  disabled={!canSlide || slideCount <= 1}
                  onClick={() => photoCarousel?.prev()}
                >
                  <ChevronLeft size={18} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={iconBtn('detail-title-next')}
                  aria-label="Next image"
                  disabled={!canSlide || slideCount <= 1}
                  onClick={() => photoCarousel?.next()}
                >
                  <ChevronRight size={18} />
                </Button>
              </div>
            </div>
            {desc ? (
              <div
                className="release-about photo-description"
                dangerouslySetInnerHTML={{ __html: linkifySocialHtml(desc) }}
              />
            ) : null}
            <PhotoLocationLink
              post={activeDetail}
              slideIndex={slideIndex}
              variant="caption"
              className="photo-location-under-caption"
            />
          </div>
        </div>
        <PhotoMetaDetails
          post={activeDetail}
          frameCount={slideCount}
          slideIndex={slideIndex}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <div className="catalog-toolbar">
          <div className="section-label">
            Photography
            <span>--</span>
          </div>
          <div className="catalog-filter is-skeleton" aria-hidden>
            <span className="skeleton skeleton-filter" />
          </div>
        </div>
        <div className="catalog-column-labels">
          <span>Year</span>
          <span>Title</span>
          <span>Format</span>
          <span />
        </div>
        <div
          className="catalog-scroll"
          ref={scrollRef}
          aria-busy="true"
          aria-label="Loading photos"
        >
          <ListSkeleton rows={estimateCatalogSkeletonRows()} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="catalog-toolbar">
        <div className="section-label">
          Photography
          <span>{items.length.toString().padStart(2, '0')}</span>
        </div>
        <div className="catalog-filter">
          <select
            aria-label="Filter catalog"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
          >
            {types.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <ChevronDown
            className="select-chevron"
            size={14}
            aria-hidden
            strokeWidth={2}
          />
        </div>
      </div>
      <div className="catalog-column-labels">
        <span>Year</span>
        <span>Title</span>
        <span>Format</span>
        <span />
      </div>
      <div
        className="catalog-scroll"
        ref={scrollRef}
        onScroll={(e) => {
          listScrollTop.current = e.currentTarget.scrollTop;
        }}
      >
        {visible.map((item, i) => {
          const year = new Date(item.date).getFullYear();
          return (
            <Link
              key={`${filterGen}-${item.id}`}
              to={paths.photoPost(item.id)}
              className={`catalog-row${filterGen > 0 ? ' catalog-row-filter-in' : ''}`}
              style={{ '--row-i': i } as CSSProperties}
              onClick={(e) => {
                e.preventDefault();
                openPhoto(item);
              }}
            >
              <span className="row-year">{Number.isFinite(year) ? year : ''}</span>
              <span className="row-title">{photoDisplayTitle(item)}</span>
              <CatalogFormatLabel label={item.format} />
              <ArrowUpRight size={16} />
            </Link>
          );
        })}
        {!visible.length ? (
          <p
            key={`empty-${filterGen}`}
            className={`empty-state${filterGen > 0 ? ' catalog-row-filter-in' : ''}`}
          >
            No results.
          </p>
        ) : null}
      </div>
    </>
  );
}
